import _ from 'lodash'
import Backbone from 'backbone'
import enums from 'scoreboard/components/enums'
import i18n from 'scoreboard/components/i18n'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import colorPickerMenu from 'scoreboard/dashboards/widgets/color-picker-menu'
import EditLabel from 'scoreboard/components/designer/edit-label'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import unwrapModel from 'scoreboard/components/unwrap-model'
import { rawColors as getColors } from 'scoreboard/components/rich-text/command-options'
import opacityMenu from '../../components/bootslide/opacity-menu'
import constants from 'scoreboard/components/constants'

const BOOTSLIDE_MENU_WIDTH = constants.BOOTSLIDE_MENU_WIDTH

/**
 * An (abstract) base class for all chart designer bootslide menus.
 *
 * It serves to provide common functionality specific to the Chart Designer and
 * offers a number of functions for sublasses to assemble the necessary menu options.
 */
const BootslideEditMenu = BootslideMenu.extend({

	/**
	 * @type ChartDesigner
	 */
	model: null,

	/**
	 * @type ChartDesignerFeature (e.g. Serie, or any analytic type)
	 */
	feature: null,

	className: 'bootslide-edit-menu',

	/**
	 * @param {ChartDesignerLineAndBand} options.labFeature Any top level model
	 * changes must be set on this object.
	 *
	 * @param {ChartDesignerFeature} options.feature A feature model reconstructed from
	 * the child component the user is interacting with.  This will be a different
	 * instance every time the bootslide menu is constructed.
	 * Any attribute changes will be copied to the parent.
	 */
	initialize: function (options = {}) {
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.options = options

		const {feature = {}, labFeature} = options
		this.chartDesigner = this.model
		this.labFeature = labFeature
		this.feature = feature
		this.allowDynamicWidth = true
	},

	render: function() {
		BootslideMenu.prototype.render.apply(this, arguments)
		this.toggleColorAndOpacity && this.showColorAndOpacityOptions()
		return this
	},

	typeLabel: function() {
		const {type} = unwrapModel(this.feature)
		return enums.chartDesignerFeatureType[type]
	},

	menu: function () { // invoked by BootslideMenu#render
		let topLevel = []
		this.addTopLevelOptions(topLevel)
		return {
			label: this.menuLabel(),
			target: topLevel,
			width: BOOTSLIDE_MENU_WIDTH
		}
	},

	menuLabel: function() {
		return i18n.get('common.edit2', this.typeLabel())
	},

	/**
	 * @abstract subclasses must add all of the options to the `topLevel` array
	 */
	addTopLevelOptions: function(topLevel) {},

	/**
	 * Edit the label of a Feature.
	 *
	 * @param {String} [options.inputLabel] ii18n code; convention based default
	 *
	 * @param {Function} [options.getLabelFn=options.feature.getLabel]
	 * Invoked to display the current label
	 *
	 * @param {Function} [options.setLabelFn=options.feature.setLabel]
	 * Invoked when user submits new label
	 */
	createEditLabelOption: function(options = {}) {
		const {
			inputLabel = i18n.get('common.labelWithPrefix', this.typeLabel()),
			getLabelFn = () => this.feature.get('label'),
			setLabelFn = label => {
				this.feature.set('label', label)
				this.feature.customLabel = true
			},
			doneKey,
			isPlaceholder,
			toggleLabel = _.get(options, 'toggleLabel', 'report.designer.labelType'),
			header = _.get(options, 'optionLabel', 'charts.feature.setLabel'),
			defaultLabel
		} = options
		return BootslideMenu.targetOption(header, () => {
			const label = getLabelFn()
			return new EditLabel({
				default: !label,
				label: label ?? defaultLabel,
				header,
				inputLabel,
				toggleLabel,
				doneKey,
				customOnly: isPlaceholder
			})
		}, setLabelFn)
	},

	createDeleteOption: function(options = {}) {
		return {
			label: i18n.get('common.delete'),
			contentView: new DeleteOption(_.extend({
				model: this.feature,
				chart: this.model
			}, options))
		}
	},

	/**
	 * @param {String} icon #40326 if present, displays an icon next to the menu
	 * option text
	 */
	createActionOption: function({label, actionFn, icon = null}) {
		const menuOption = {
			label: i18n.get(label),
			target: e => {
				actionFn(e)
			}
		}

		if (icon) {
			menuOption.contentView = Backbone.View.extend({
				render: function() {
					this.$el.html(BootslideMenu.configIconLabel(icon, label))
					return this
				}
			})
		}

		return menuOption

	},

	createColorMenu: function(removeWhite = false) {
		const designerModel = this.model
		const colors = getColors()
		if (removeWhite) {
			// #38914 This is a bit of overkill since we know the exact index of 'white',
			// but this is more future proof in case the order changes
			_.forEach(colors, row => {
				_.remove(row, c => c === 'white')
			})
		}
		return colorPickerMenu(this, designerModel, {
			label: 'charts.chart.color',
			source: {
				model: this.feature,
				property: 'color'
			},
			colors,
			onSelect: (color) => {
				designerModel.setFeatureColor(this.feature, color)
			}
		})
	},

	createShowOption: function() {
		const {enabled} = unwrapModel(this.feature)
		const label = i18n.get('common.showWithArg', this.typeLabel())
		this.showToggle = new OnOffToggle({
			label,
			name: 'show',
			checked: enabled,
			isMini: true
		})
		this.showToggle.on('change', val => {
			this.feature.set('enabled', val)
			this.trigger('show', val)
		})
		return {
			label,
			contentView: this.showToggle,
			classNames: 'show-toggle'
		}
	},

	createCopyOption: function(toCopy, modelConstructor) {
		return this.createActionOption({
			label: 'common.duplicate',
			actionFn: () => {
				this.model.addCopy(toCopy, modelConstructor)
				this.trigger('done')
			},
			icon: 'copy'
		})
	},

	showColorAndOpacityOptions: function() {
		let show = _.invoke(this.feature, 'get', 'enabled')
		show = _.isNil(show) ? true : show
		this.bootslide.toggleItem('color', show)
		this.bootslide.toggleItem('opacity', show)
	},

	addColorAndOpacityOptions: function(topLevel) {
		topLevel.push(_.extend(this.createColorMenu(), { id: 'color' }))
		topLevel.push(_.extend(
			opacityMenu({ model: this.feature, showMaxOpacity: true }),
			{ id: 'opacity'}))
		this.on('show', this.showColorAndOpacityOptions.bind(this))
		this.toggleColorAndOpacity = true
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.showToggle], 'remove')
	}

}, {
	BOOTSLIDE_MENU_WIDTH
})

/**
 * @private
 */
const DeleteOption = BootslideMenu.DeleteNode.extend({

	/**
	 * @constructs
	 *
	 * @param {ChartDesignerFeature} options.model
	 *
	 * @param {ChartDesigner} options.chart
	 *
	 * @param {Function} [options.deleteFn=chart.deleteFeature()] Default is fine
	 * for top level features, but children might need to be handled differently.
	 */
	initialize: function(options = {}) {
		this.chart = options.chart
		this.deleteFn = options.deleteFn || (() => this.chart.deleteFeature(this.model))
	},

	/**
	 * @override
	 */
	doDelete: function () {
		this.deleteFn()
		this.trigger('done')
	}
})

export default BootslideEditMenu
BootslideEditMenu.__filename__ = 'charts/edit/bootslide-edit-menu.js'
